import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { first } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { EmbeddedActions } from './embedded.actions';
import { DealService } from '../../features/deal/services/deal.service';
import { getDocumentTransactionLibraryId } from '../../features/document/constants/document-helper';
import { DocumentViewerActions } from '../document-viewer/document-viewer.actions';
import { RootState } from '../root-state.model';
import { getAllRouteParameters } from '../router/router.selector';

@Injectable()
export class EmbeddedEffects {
  private service = inject(DealService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);

  loadDeal$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(EmbeddedActions.LoadDeal),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        const paramMap = getAllRouteParameters(store.router.state);

        return this.service
          .getById(
            paramMap.get('libraryShortName'),
            paramMap.get('managerName'),
            paramMap.get('dealShortName')
          )
          .pipe(
            map((deal) => EmbeddedActions.LoadDealSuccess({ deal })),
            catchError((error) => of(EmbeddedActions.LoadDealFail({ error: error?.toString() })))
          );
      })
    )
  );

  openOCDocument$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(EmbeddedActions.LoadDealSuccess),
      filter((action) => !!action.deal),
      map(({ deal }) => {
        const document =
          deal.documents.find((d) => d.documentId === deal.firstDisplayDocument) ||
          deal.documents.find((d) => d.documentId.endsWith('-oc')) ||
          first(deal.documents);
        if (document) {
          return DocumentViewerActions.OpenDocument({
            documentId: document.documentId,
            documentTransactionLibraryId: getDocumentTransactionLibraryId(
              document.documentId,
              deal.transactionId
            ),
          });
        } else {
          // TODO: Handle this path
          //return PanelActions.SetCalculatedViewerState({ state: [PanelViewId.Summary] });
        }
      })
    )
  );
}
